import { notifyAuthExpired } from "./authStatus";
import { parseErrorResponse } from "./apiError";
import { buildApiUrl } from "../config/api";
import { createLogger } from "./logger";
import { reauthSilently } from "../auth/silentReauth";
import { isDomAbortError } from "./abortError";
import { CLIENT_PROTOCOL_VERSION } from "../clientUpdate/buildVersion";
import { notifyUpgradeRequired } from "../clientUpdate/upgradeRequired";

const log = createLogger("HTTP");
const RETRY_BASE_DELAY_MS = 2000;

function getErrorMessage(err: unknown): string {
  if (err instanceof Error) return err.message;
  return String(err);
}

export async function readModelError(response: Response, contextLabel: string): Promise<string> {
  if (response.status === 401) {
    notifyAuthExpired();
  }
  const message = await parseErrorResponse(response);
  return `${contextLabel}: ${response.status} - ${message}`;
}

async function refreshSession(): Promise<boolean> {
  try {
    log.debug("[Auth] Attempting session refresh...");
    const response = await fetch(buildApiUrl("/api/auth/session"), {
      method: "GET",
      credentials: "include",
    });
    const success = response.ok;
    log.debug(`[Auth] Session refresh ${success ? "successful" : "failed"} (${response.status})`);
    return success;
  } catch (error) {
    log.warn("[Auth] Session refresh error:", error);
    return false;
  }
}

async function isRefreshableAuthError(response: Response): Promise<boolean> {
  if (response.status === 401) return true;
  if (response.status !== 403) return false;

  try {
    const data = await response.clone().json();
    return data?.error !== "upstream_unauthorized";
  } catch {
    return true;
  }
}

const MAX_BACKOFF_MS = 30_000;
const JITTER_CEILING_MS = 500;
const PER_REQUEST_TIMEOUT_MS = 10 * 60 * 1000;
const FETCH_TIMEOUT_FLAG = "__fetchWithRetryTimeout__";
// The Response constructor rejects a body for these statuses, and Chromium still hands
// back a non-null (empty) stream for them, so they must not be re-wrapped.
const NULL_BODY_STATUSES = new Set([101, 103, 204, 205, 304]);

function timeoutFetchError(timeoutMs: number): Error {
  const timeoutErr = new Error(
    `Request timed out after ${(timeoutMs / 1000).toFixed(0)}s`,
  ) as Error & { [FETCH_TIMEOUT_FLAG]?: boolean };
  timeoutErr[FETCH_TIMEOUT_FLAG] = true;
  return timeoutErr;
}

function lifecycleAbortError(timedOut: boolean, timeoutMs: number): Error {
  if (timedOut) return timeoutFetchError(timeoutMs);
  const error = new Error("The operation was aborted");
  error.name = "AbortError";
  return error;
}

function wrapResponseWithLifecycle(
  response: Response,
  options: {
    signal: AbortSignal;
    timedOut: () => boolean;
    timeoutMs: number;
    cleanup: () => void;
  },
): Response {
  const source = response.body;
  if (!source) {
    options.cleanup();
    return response;
  }
  const reader = source.getReader();
  let settled = false;
  const settle = (): void => {
    if (settled) return;
    settled = true;
    options.cleanup();
  };
  const body = new ReadableStream<Uint8Array>({
    async pull(controller) {
      try {
        if (options.signal.aborted) {
          throw lifecycleAbortError(options.timedOut(), options.timeoutMs);
        }
        const next = await new Promise<ReadableStreamReadResult<Uint8Array>>(
          (resolve, reject) => {
            const onAbort = (): void => {
              reject(lifecycleAbortError(options.timedOut(), options.timeoutMs));
            };
            options.signal.addEventListener("abort", onAbort, { once: true });
            void reader.read().then(resolve, reject).finally(() => {
              options.signal.removeEventListener("abort", onAbort);
            });
          },
        );
        if (next.done) {
          settle();
          controller.close();
          return;
        }
        controller.enqueue(next.value);
      } catch (error) {
        settle();
        void reader.cancel(error).catch(() => undefined);
        controller.error(options.timedOut() ? timeoutFetchError(options.timeoutMs) : error);
      }
    },
    async cancel(reason) {
      settle();
      try {
        await reader.cancel(reason);
      } catch {
        // Best-effort cancellation; the caller already abandoned the body.
      }
    },
  });
  return new Response(body, {
    status: response.status,
    statusText: response.statusText,
    headers: response.headers,
  });
}

async function cancelResponseBody(response: Response): Promise<void> {
  try {
    await response.body?.cancel();
  } catch {
    // Best-effort cleanup before a retry.
  }
}

export function isTimeoutFetchError(err: unknown): boolean {
  return (
    typeof err === "object" &&
    err !== null &&
    (err as { [FETCH_TIMEOUT_FLAG]?: boolean })[FETCH_TIMEOUT_FLAG] === true
  );
}

export function isTransientFetchError(err: unknown): boolean {
  if (isTimeoutFetchError(err)) return true;
  if (!(err instanceof Error)) return false;
  return (
    err.message === "Load failed" ||
    err.message.includes("network") ||
    err.message.includes("timeout") ||
    err.message.includes("Failed to fetch")
  );
}

export async function fetchWithRetry(
  url: string,
  options: RequestInit,
  maxRetries: number = 2,
  logTag: string = "API",
  requestTimeoutMs: number = PER_REQUEST_TIMEOUT_MS,
): Promise<Response> {
  const userSignal = options.signal ?? undefined;

  let lastError: Error | null = null;
  let hasAttemptedRefresh = false;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    const startTime = Date.now();

    const timeoutController = new AbortController();
    let timedOut = false;
    const timeoutId = setTimeout(() => {
      timedOut = true;
      timeoutController.abort();
    }, requestTimeoutMs);

    const onUserAbort = () => timeoutController.abort();
    if (userSignal) {
      if (userSignal.aborted) timeoutController.abort();
      else userSignal.addEventListener("abort", onUserAbort, { once: true });
    }

    const headers = new Headers(options.headers);
    headers.set("X-Finster-Client-Protocol", String(CLIENT_PROTOCOL_VERSION));
    const fetchInit: RequestInit = {
      ...options,
      headers,
      signal: timeoutController.signal,
    };
    let lifecycleTransferred = false;
    const cleanupLifecycle = (): void => {
      clearTimeout(timeoutId);
      if (userSignal) userSignal.removeEventListener("abort", onUserAbort);
    };
    const handOffResponse = (response: Response): Response => {
      if (!response.body || NULL_BODY_STATUSES.has(response.status)) {
        cleanupLifecycle();
        return response;
      }
      lifecycleTransferred = true;
      return wrapResponseWithLifecycle(response, {
        signal: timeoutController.signal,
        timedOut: () => timedOut,
        timeoutMs: requestTimeoutMs,
        cleanup: cleanupLifecycle,
      });
    };

    try {
      if (attempt > 0) {
        log.debug(`[${logTag}] Retry ${attempt}/${maxRetries}...`);
      }

      const response = await fetch(url, fetchInit);
      const elapsedSec = ((Date.now() - startTime) / 1000).toFixed(1);
      log.debug(`[${logTag}] ${response.status} in ${elapsedSec}s`);

      if (response.status === 426) {
        await notifyUpgradeRequired(response);
        return handOffResponse(response);
      }

      if (!hasAttemptedRefresh && (await isRefreshableAuthError(response))) {
        log.debug(`[${logTag}] Auth error ${response.status}, attempting session refresh...`);
        hasAttemptedRefresh = true;

        const refreshed = await refreshSession();
        if (refreshed) {
          if (attempt < maxRetries) {
            log.debug(`[${logTag}] Session refreshed, retrying...`);
            await cancelResponseBody(response);
            continue;
          }
          log.debug(
            `[${logTag}] Session refreshed but retry budget exhausted; returning original auth response.`,
          );
          return handOffResponse(response);
        }

        log.debug(`[${logTag}] Session refresh failed; attempting silent SSO reauth...`);
        const reauthState = await reauthSilently();
        if (reauthState?.isAuthenticated && attempt < maxRetries) {
          log.debug(`[${logTag}] Silent reauth succeeded, retrying...`);
          await cancelResponseBody(response);
          continue;
        }

        log.debug(`[${logTag}] Silent reauth unavailable; surfacing 401.`);
        notifyAuthExpired();
        return handOffResponse(response);
      }

      if (response.status === 401) {
        notifyAuthExpired();
      }

      return handOffResponse(response);
    } catch (err) {
      const elapsedSec = ((Date.now() - startTime) / 1000).toFixed(1);

      if (isDomAbortError(err)) {
        if (!timedOut) throw err;
        const timeoutErr = timeoutFetchError(requestTimeoutMs);
        lastError = timeoutErr;
        log.warn(`[${logTag}] Attempt ${attempt + 1} timed out after ${elapsedSec}s`);
      } else {
        lastError = err instanceof Error ? err : new Error(getErrorMessage(err));
        log.warn(
          `[${logTag}] Attempt ${attempt + 1} failed after ${elapsedSec}s: ${lastError.message}`,
        );
        if (!isTransientFetchError(lastError)) throw lastError;
      }

      if (attempt >= maxRetries) throw lastError;

      const jitter = Math.floor(Math.random() * JITTER_CEILING_MS);
      const delayMs = Math.min(RETRY_BASE_DELAY_MS * Math.pow(2, attempt) + jitter, MAX_BACKOFF_MS);
      log.debug(`[${logTag}] Waiting ${delayMs}ms before retry...`);
      await new Promise((r) => setTimeout(r, delayMs));
    } finally {
      if (!lifecycleTransferred) cleanupLifecycle();
    }
  }

  throw lastError || new Error("Fetch failed");
}
